
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { NewCandidateFormData } from "@/components/candidates/types";
import { trackCandidateCreated as trackCandidateCreatedAnalytics } from '@/utils/analytics';
import { toast } from 'sonner';
import { ResumeValidator } from '@/utils/resumeValidation';

export function useCreateCandidate() {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    any,
    Error,
    NewCandidateFormData
  >({
    mutationFn: async (newCandidateData: NewCandidateFormData) => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Enhanced file validation
      if (newCandidateData.resume_file) {
        const validationResult = ResumeValidator.validateFileUpload(newCandidateData.resume_file);
        if (!validationResult.isValid) {
          throw new Error(validationResult.error || 'File validation failed');
        }
        
        if (validationResult.warnings && validationResult.warnings.length > 0) {
          console.warn('File validation warnings:', validationResult.warnings);
          toast.warning(`File uploaded with warnings: ${validationResult.warnings.join(', ')}`);
        }
      }

      // Prepare candidate data for insertion
      const candidateData = {
        first_name: newCandidateData.first_name,
        last_name: newCandidateData.last_name,
        email: newCandidateData.email,
        phone: newCandidateData.phone,
        source: newCandidateData.source,
        status: newCandidateData.status || 'new',
        user_id: user.id,
      };

      // Insert candidate
      const { data: candidate, error: candidateError } = await supabase
        .from('candidates')
        .insert(candidateData)
        .select()
        .single();

      if (candidateError) throw candidateError;

      // Handle resume upload if file is provided
      let resumeUrl = null;
      if (newCandidateData.resume_file) {
        const fileExt = newCandidateData.resume_file.name.split('.').pop();
        const fileName = `${user.id}/${candidate.id}/resume.${fileExt}`;
        
        try {
          const { error: uploadError } = await supabase.storage
            .from('resumes')
            .upload(fileName, newCandidateData.resume_file);

          if (uploadError) {
            console.error('Resume upload error:', uploadError);
            toast.error('Resume upload failed, but candidate was created');
          } else {
            const { data: { publicUrl } } = supabase.storage
              .from('resumes')
              .getPublicUrl(fileName);
            
            resumeUrl = publicUrl;
            
            const { error: updateError } = await supabase
              .from('candidates')
              .update({ resume_url: resumeUrl })
              .eq('id', candidate.id);

            if (updateError) {
              console.error('Resume URL update error:', updateError);
            }

            // Trigger enhanced OCR processing for the uploaded resume
            try {
              console.log('Starting enhanced OCR processing for candidate:', candidate.id);
              
              const { error: ocrError } = await supabase.functions.invoke('process-resume-ocr', {
                body: {
                  candidateId: candidate.id,
                  resumeUrl: resumeUrl,
                  userId: user.id,
                  retryAttempt: false
                }
              });

              if (ocrError) {
                console.error('Failed to start enhanced OCR processing:', ocrError);
                toast.info('Candidate created. Enhanced resume processing will start shortly.');
              } else {
                toast.success('Candidate created! Enhanced AI resume analysis with validation in progress...');
              }
            } catch (error) {
              console.error('Error triggering enhanced OCR processing:', error);
              toast.info('Candidate created. Enhanced resume processing will start shortly.');
            }
          }
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          toast.error('Resume upload failed, but candidate was created');
        }
      }

      // Create job application if job is selected
      if (newCandidateData.job_id) {
        const { error: applicationError } = await supabase
          .from('job_applications')
          .insert({
            job_id: newCandidateData.job_id,
            candidate_id: candidate.id,
            status: 'submitted',
            applied_at: new Date().toISOString(),
          });

        if (applicationError) {
          console.error('Job application creation error:', applicationError);
        }
      }

      return { ...candidate, resume_url: resumeUrl };
    },
    onSuccess: (createdCandidate) => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['job-applications'] });
      
      // Track GA event
      trackCandidateCreatedAnalytics({
        id: createdCandidate.id,
        first_name: createdCandidate.first_name,
        last_name: createdCandidate.last_name,
        status: createdCandidate.status,
      });
    },
    onError: (error) => {
      toast.error(`Failed to create candidate: ${error.message}`);
      console.error("Mutation error in useCreateCandidate:", error);
    },
  });

  return {
    createCandidate: mutate,
    isCreatingCandidate: isPending,
  };
}
